import React, { useEffect, useRef, useState } from "react";

const SLIDE_INTERVAL_MS = 3000;
const SLIDE_TRANSITION_MS = 600;

function computePerView(width) {
  if (width < 640) return 1;
  if (width < 960) return 2;
  return 3;
}

export default function TestimonialsCarousel({ items }) {
  const [perView, setPerView] = useState(() =>
    typeof window !== "undefined" ? computePerView(window.innerWidth) : 3,
  );
  const [index, setIndex] = useState(0);
  const [animating, setAnimating] = useState(true);
  const [paused, setPaused] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    const update = () => setPerView(computePerView(window.innerWidth));
    window.addEventListener("resize", update);
    window.addEventListener("orientationchange", update);
    return () => {
      window.removeEventListener("resize", update);
      window.removeEventListener("orientationchange", update);
    };
  }, []);

  useEffect(() => {
    if (paused) return;
    if (items.length <= perView) return;
    timerRef.current = setInterval(() => {
      setIndex((i) => i + 1);
    }, SLIDE_INTERVAL_MS);
    return () => clearInterval(timerRef.current);
  }, [items.length, perView, paused]);

  useEffect(() => {
    if (items.length === 0) return;
    if (index < items.length) return;
    const snap = setTimeout(() => {
      setAnimating(false);
      setIndex(0);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => setAnimating(true));
      });
    }, SLIDE_TRANSITION_MS);
    return () => clearTimeout(snap);
  }, [index, items.length]);

  useEffect(() => {
    setIndex(0);
  }, [perView]);

  if (!items.length) return null;

  const canLoop = items.length > perView;
  const slideWidthPct = 100 / perView;
  const displayList = canLoop ? [...items, ...items.slice(0, perView)] : items;
  const translatePct = canLoop ? -index * slideWidthPct : 0;
  const activeDot = index % items.length;

  const goTo = (i) => {
    setIndex(i);
    setPaused(true);
    setTimeout(() => setPaused(false), 4000);
  };

  return (
    <div>
      <div
        style={{ overflow: "hidden", width: "100%" }}
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <div
          style={{
            display: "flex",
            alignItems: "stretch",
            justifyContent: canLoop ? "flex-start" : "center",
            transform: `translateX(${translatePct}%)`,
            transition:
              canLoop && animating
                ? `transform ${SLIDE_TRANSITION_MS}ms ease`
                : "none",
            willChange: "transform",
          }}
        >
          {displayList.map((e, i) => (
            <div
              key={`${e.name}-${i}`}
              style={{
                flex: `0 0 ${slideWidthPct}%`,
                boxSizing: "border-box",
                padding: "0 12px",
                display: "flex",
              }}
            >
              <div className="endorse-card" style={{ width: "100%" }}>
                <div className="endorse-mark">"</div>
                <p className="endorse-text">{e.text}</p>
                <div className="endorse-person">
                  <img src={e.img} alt={e.name} className="endorse-avatar" />
                  <div>
                    <p className="endorse-name">{e.name}</p>
                    <p className="endorse-role">{e.role}</p>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Dot navigation */}
      {items.length > 1 && (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginTop: "2rem",
          }}
        >
          {items.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              aria-label={`Go to slide ${i + 1}`}
              style={{
                width: i === activeDot ? "24px" : "8px",
                height: "8px",
                borderRadius: "100px",
                border: "none",
                cursor: "pointer",
                padding: 0,
                background:
                  i === activeDot
                    ? "var(--accent)"
                    : "var(--line-strong)",
                opacity: i === activeDot ? 1 : 0.25,
                transition: "width 300ms ease, background 300ms ease, opacity 300ms ease",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}
